import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISector } from '../types/Monitor';

export interface CallItem {
  sectorAndTrackNumber: string;
  customerName: string;
  showed: boolean;
}

export interface CallQueueState {
  queue: CallItem[];
  current: ISector | null;
  hasNext: boolean;
}

const initialState: CallQueueState = {
  queue: [],
  current: null,
  hasNext: false,
};

export const selectHasNext = (queue: CallItem[], current: ISector | null) =>
  queue.some(
    (item) =>
      !item.showed &&
      item.sectorAndTrackNumber !==
      `${current?.sectorName}-${current?.trackNumber}`
  );

const keepActiveCalls = (queue: CallItem[], action: string[]) =>
  queue.filter((item) => action.includes(item.sectorAndTrackNumber));

const callQueueSlice = createSlice({
  name: 'callQueue',
  initialState,
  reducers: {
    syncActiveCalls: (state, action: PayloadAction<ISector[]>) => {
      const incoming = action.payload;
      const incomingIds = incoming.map(
        (s) => `${s.sectorName}-${s.trackNumber}`
      );

      state.queue = keepActiveCalls(state.queue, incomingIds);

      const newItems = incoming.filter(
        (sector) =>
          !state.queue.some(
            (item) =>
              item.sectorAndTrackNumber ===
              `${sector.sectorName}-${sector.trackNumber}`
          )
      );

      state.queue.push(
        ...newItems.map((sector) => ({
          sectorAndTrackNumber: `${sector.sectorName}-${sector.trackNumber}`,
          customerName: sector.customerName,
          showed: false,
        }))
      );

      state.hasNext = selectHasNext(state.queue, state.current);
    },
    markAsShowed: (state) => {
      const current = state.current;
      if (!current) return;

      const currentSelected = `${current.sectorName}-${current.trackNumber}`;
      const selectedCallItem = state.queue.find(
        (sector) => sector.sectorAndTrackNumber === currentSelected
      );
      if (selectedCallItem) {
        selectedCallItem.showed = true;
      }

      state.hasNext = selectHasNext(state.queue, state.current);
    },
    nextCall: (state) => {
      const next = state.queue.find((sector) => !sector.showed);

      if (!next) {
        state.current = null;
        return;
      }

      const [sectorName, trackNumber] = next.sectorAndTrackNumber.split('-');

      state.current = {
        sectorName,
        trackNumber,
        customerName: next.customerName,
        status: 'CALLING',
      };

      state.hasNext = selectHasNext(state.queue, state.current);
    },
  },
});

export const { syncActiveCalls, markAsShowed, nextCall } =
  callQueueSlice.actions;
export const callQueueReducer = callQueueSlice.reducer;
